
import { useState } from 'react';
import Button from '../../components/base/Button';
import SEOHead from '../../components/seo/SEOHead';

export default function ServicesPage() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedService, setSelectedService] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const whatsappMessage = encodeURIComponent("Hello 👋 The Tooth Doctor,\nI would like to book an appointment / make an enquiry about your dental services.\nPlease share available slots and details.");
  const whatsappUrl = `https://wa.me/916369090441?text=${whatsappMessage}`;

  const serviceCategories = [
    { id: 'all', name: 'All Services' },
    { id: 'general', name: 'General Dentistry' },
    { id: 'restorative', name: 'Restorative Dentistry' },
    { id: 'cosmetic', name: 'Cosmetic & Aesthetic' },
    { id: 'pediatric', name: 'Pediatric Care' },
    { id: 'orthodontic', name: 'Orthodontics' },
    { id: 'surgical', name: 'Oral Surgery' },
    { id: 'implants', name: 'Implants & Prosthetics' },
    { id: 'periodontics', name: 'Gum Care & Laser' },
    { id: 'advanced', name: 'Advanced & Preventive' },
    { id: 'specialized', name: 'Specialized Services' }
  ];

  const services = [
    // General Dentistry
    {
      id: 1,
      title: 'Dental Check-Up & Consultation',
      category: 'general',
      description: 'Regular check-ups help detect oral problems early. Our dentists evaluate your teeth, gums, and bite using digital X-rays and intraoral cameras.',
      features: ['Digital X-rays', 'Intraoral cameras', 'Early detection', 'Preventive care'],
      image: 'https://readdy.ai/api/search-image?query=Indian%20dentist%20examining%20Indian%20patient%20teeth%2C%20dental%20checkup%20in%20Chennai%20clinic%2C%20professional%20dental%20consultation%20with%20Indian%20doctor%20and%20patient%2C%20modern%20dental%20examination&width=400&height=300&seq=dental-checkup-indian&orientation=landscape',
      icon: 'ri-stethoscope-line',
      duration: '30-45 minutes',
      procedure: 'Visual examination, digital X-rays, oral cancer screening, gum health assessment, bite analysis, and personalized treatment planning.',
      benefits: ['Early problem detection', 'Prevent costly treatments', 'Maintain oral health', 'Professional cleaning', 'Personalized care plan'],
      aftercare: 'Follow recommended oral hygiene routine, schedule regular cleanings every 6 months, and address any concerns promptly.'
    },
    {
      id: 2,
      title: 'Dental Cleaning (Scaling & Polishing)',
      category: 'general',
      description: 'Professional scaling removes plaque and tartar buildup to prevent gum disease, cavities, and bad breath. Polishing smoothens your teeth.',
      features: ['Plaque removal', 'Tartar cleaning', 'Fresh breath', 'Smooth teeth'],
      image: 'https://readdy.ai/api/search-image?query=Indian%20dental%20hygienist%20cleaning%20Indian%20patient%20teeth%2C%20professional%20scaling%20and%20polishing%20in%20Chennai%20dental%20clinic%2C%20ultrasonic%20scaler%20treatment&width=400&height=300&seq=dental-cleaning-indian&orientation=landscape',
      icon: 'ri-brush-line',
      duration: '45-60 minutes',
      procedure: 'Ultrasonic scaling to remove tartar, manual scaling for stubborn deposits, polishing with special paste, and fluoride treatment.',
      benefits: ['Prevents gum disease', 'Removes stains', 'Fresh breath', 'Smooth tooth surface', 'Improved oral health'],
      aftercare: 'Avoid eating for 30 minutes after fluoride treatment, maintain good oral hygiene, and schedule cleanings every 6 months.'
    },
    {
      id: 3,
      title: 'Tooth-Colored Fillings',
      category: 'general',
      description: 'We restore decayed or damaged teeth using tooth-colored composite fillings that blend naturally with your smile — strong, aesthetic, and mercury-free.',
      features: ['Natural appearance', 'Mercury-free', 'Strong material', 'Aesthetic blend'],
      image: 'https://readdy.ai/api/search-image?query=Indian%20dentist%20placing%20tooth%20colored%20composite%20filling%20on%20Indian%20patient%2C%20white%20filling%20procedure%20in%20Chennai%20dental%20clinic%2C%20aesthetic%20dental%20restoration&width=400&height=300&seq=tooth-filling-indian&orientation=landscape',
      icon: 'ri-drop-line',
      duration: '30-60 minutes',
      procedure: 'Local anesthesia, decay removal, tooth preparation, composite application in layers, shaping, and polishing.',
      benefits: ['Natural appearance', 'Mercury-free safety', 'Strong and durable', 'Preserves tooth structure', 'Immediate use'],
      aftercare: 'Avoid hard foods for 24 hours, maintain good oral hygiene, and avoid staining foods initially.'
    },

    // Restorative Dentistry
    {
      id: 4,
      title: 'Root Canal Treatment (Endodontics)',
      category: 'restorative',
      description: 'A precise, pain-free procedure to treat tooth infections and save natural teeth. Using rotary endodontics and digital imaging for comfort.',
      features: ['Pain-free procedure', 'Rotary endodontics', 'Digital imaging', 'Save natural teeth'],
      image: 'https://readdy.ai/api/search-image?query=Indian%20endodontist%20performing%20root%20canal%20treatment%20on%20Indian%20patient%2C%20rotary%20endodontics%20procedure%20in%20Chennai%20clinic%2C%20pain-free%20dental%20treatment&width=400&height=300&seq=root-canal-indian&orientation=landscape',
      icon: 'ri-heart-pulse-line',
      duration: '60-90 minutes',
      procedure: 'Local anesthesia, access opening, infected pulp removal, canal cleaning and shaping, disinfection, and temporary filling.',
      benefits: ['Saves natural tooth', 'Eliminates pain', 'Prevents extraction', 'Maintains chewing function', 'Cost-effective'],
      aftercare: 'Take prescribed medications, avoid hard foods, maintain oral hygiene, and return for crown placement.'
    },
    {
      id: 5,
      title: 'Dental Crowns & Bridges',
      category: 'restorative',
      description: 'Custom-made crowns restore damaged teeth, while bridges fill gaps caused by missing teeth — improving both appearance and function.',
      features: ['Custom-made', 'Natural appearance', 'Improved function', 'Long-lasting'],
      image: 'https://readdy.ai/api/search-image?query=Indian%20prosthodontist%20placing%20dental%20crown%20on%20Indian%20patient%2C%20custom%20crown%20and%20bridge%20procedure%20in%20Chennai%20dental%20clinic%2C%20tooth%20restoration&width=400&height=300&seq=crowns-bridges-indian&orientation=landscape',
      icon: 'ri-building-line',
      duration: '2-3 visits over 2 weeks',
      procedure: 'Tooth preparation, impression taking, temporary crown placement, custom crown fabrication, and final cementation.',
      benefits: ['Restores tooth function', 'Natural appearance', 'Long-lasting solution', 'Protects weak teeth', 'Improves bite'],
      aftercare: 'Avoid sticky foods initially, maintain good oral hygiene, use soft toothbrush, and attend regular checkups.'
    },
    {
      id: 6,
      title: 'Dental Inlays & Onlays',
      category: 'restorative',
      description: 'Ideal for moderately damaged teeth, inlays and onlays provide a conservative alternative to full crowns while maintaining tooth structure.',
      features: ['Conservative treatment', 'Preserve tooth structure', 'Precise fit', 'Durable restoration'],
      image: 'https://readdy.ai/api/search-image?query=Indian%20dentist%20placing%20dental%20inlay%20on%20Indian%20patient%2C%20conservative%20tooth%20restoration%20in%20Chennai%20clinic%2C%20porcelain%20inlay%20procedure&width=400&height=300&seq=inlays-onlays-indian&orientation=landscape',
      icon: 'ri-puzzle-line',
      duration: '2 visits over 1-2 weeks',
      procedure: 'Decay removal, precise tooth preparation, digital impression, custom fabrication, and bonding.',
      benefits: ['Preserves tooth structure', 'Precise fit', 'Durable material', 'Natural appearance', 'Conservative approach'],
      aftercare: 'Avoid hard foods for 24 hours, maintain oral hygiene, and schedule regular checkups.'
    },

    // Cosmetic & Aesthetic Dentistry
    {
      id: 7,
      title: 'Teeth Whitening',
      category: 'cosmetic',
      description: 'Get a radiant smile with our safe, professional whitening system. Removes stains from coffee, tea, or tobacco and brightens teeth in one sitting.',
      features: ['Professional grade', 'Safe procedure', 'Immediate results', 'Stain removal'],
      image: 'https://readdy.ai/api/search-image?query=Indian%20dentist%20performing%20teeth%20whitening%20on%20Indian%20patient%2C%20professional%20dental%20bleaching%20in%20Chennai%20clinic%2C%20bright%20white%20smile%20enhancement&width=400&height=300&seq=teeth-whitening-indian&orientation=landscape',
      icon: 'ri-flashlight-line',
      duration: '60-90 minutes',
      procedure: 'Gum protection, professional whitening gel application, light activation, multiple cycles, and final assessment.',
      benefits: ['Immediate results', 'Safe and effective', 'Removes deep stains', 'Boosts confidence', 'Professional grade'],
      aftercare: 'Avoid staining foods for 48 hours, use whitening toothpaste, avoid smoking, and maintain good oral hygiene.'
    },
    {
      id: 8,
      title: 'Dental Veneers & Laminates',
      category: 'cosmetic',
      description: 'Porcelain or composite veneers enhance your smile by correcting chipped, discolored, or misaligned teeth with a natural-looking finish.',
      features: ['Porcelain/composite', 'Natural finish', 'Correct imperfections', 'Smile enhancement'],
      image: 'https://readdy.ai/api/search-image?query=Indian%20cosmetic%20dentist%20applying%20dental%20veneers%20on%20Indian%20patient%2C%20porcelain%20veneer%20procedure%20in%20Chennai%20clinic%2C%20smile%20makeover%20treatment&width=400&height=300&seq=dental-veneers-indian&orientation=landscape',
      icon: 'ri-palette-line',
      duration: '2-3 visits over 2-3 weeks',
      procedure: 'Consultation and planning, minimal tooth preparation, impression taking, custom fabrication, and bonding.',
      benefits: ['Natural appearance', 'Stain resistant', 'Corrects multiple issues', 'Long-lasting', 'Minimal tooth removal'],
      aftercare: 'Avoid hard foods, maintain oral hygiene, wear night guard if needed, and attend regular checkups.'
    },
    {
      id: 9,
      title: 'Smile Design & Makeovers',
      category: 'cosmetic',
      description: 'A customized aesthetic plan combining veneers, whitening, contouring, and alignment to give you a confident, camera-ready smile.',
      features: ['Customized plan', 'Multiple treatments', 'Camera-ready smile', 'Confidence boost'],
      image: 'https://readdy.ai/api/search-image?query=Indian%20cosmetic%20dentist%20planning%20smile%20makeover%20for%20Indian%20patient%2C%20digital%20smile%20design%20in%20Chennai%20clinic%2C%20complete%20smile%20transformation&width=400&height=300&seq=smile-makeover-indian&orientation=landscape',
      icon: 'ri-star-line',
      duration: 'Multiple visits over 4-8 weeks',
      procedure: 'Comprehensive assessment, digital smile design, treatment planning, phased treatment execution, and final adjustments.',
      benefits: ['Complete transformation', 'Customized approach', 'Improved confidence', 'Harmonious smile', 'Long-term results'],
      aftercare: 'Follow all post-treatment instructions, maintain excellent oral hygiene, wear retainers if provided, and attend follow-ups.'
    },
    {
      id: 10,
      title: 'Tooth Jewellery',
      category: 'cosmetic',
      description: 'Add a sparkle to your smile with safe, non-invasive tooth accessories applied by professionals.',
      features: ['Safe application', 'Non-invasive', 'Professional service', 'Sparkle effect'],
      image: 'https://readdy.ai/api/search-image?query=Indian%20dentist%20applying%20tooth%20jewelry%20on%20Indian%20patient%2C%20dental%20gems%20application%20in%20Chennai%20clinic%2C%20cosmetic%20tooth%20decoration&width=400&height=300&seq=tooth-jewelry-indian&orientation=landscape',
      icon: 'ri-gem-line',
      duration: '15-30 minutes',
      procedure: 'Tooth cleaning, surface preparation, adhesive application, gem placement, and curing.',
      benefits: ['Unique style', 'Non-invasive', 'Removable', 'Safe materials', 'Instant glamour'],
      aftercare: 'Avoid hard foods initially, maintain oral hygiene carefully around gem, and return if gem loosens.'
    },

    // Pediatric Dentistry
    {
      id: 11,
      title: 'Preventive Dental Care for Children',
      category: 'pediatric',
      description: 'Regular checkups, fluoride applications, sealants, and guidance on brushing to ensure a lifetime of healthy smiles.',
      features: ['Regular checkups', 'Fluoride treatment', 'Dental sealants', 'Brushing guidance'],
      image: 'https://readdy.ai/api/search-image?query=Indian%20pediatric%20dentist%20examining%20Indian%20child%20patient%2C%20kid-friendly%20dental%20clinic%20in%20Chennai%2C%20colorful%20children%20dental%20care%20environment&width=400&height=300&seq=pediatric-care-indian&orientation=landscape',
      icon: 'ri-user-smile-line',
      duration: '30-45 minutes',
      procedure: 'Gentle examination, cleaning, fluoride application, sealant placement if needed, and oral hygiene education.',
      benefits: ['Prevents cavities', 'Builds good habits', 'Early problem detection', 'Comfortable experience', 'Educational'],
      aftercare: 'Help child brush twice daily, limit sugary snacks, encourage healthy eating, and schedule regular visits.'
    },
    {
      id: 12,
      title: 'Pulp Therapy & Space Maintainers',
      category: 'pediatric',
      description: 'Treatment for infected baby teeth and maintaining proper space for permanent teeth eruption.',
      features: ['Baby tooth treatment', 'Space maintenance', 'Permanent tooth guidance', 'Infection control'],
      image: 'https://readdy.ai/api/search-image?query=Indian%20pediatric%20dentist%20performing%20pulp%20therapy%20on%20Indian%20child%2C%20space%20maintainer%20placement%20in%20Chennai%20kids%20dental%20clinic&width=400&height=300&seq=pulp-therapy-indian&orientation=landscape',
      icon: 'ri-seedling-line',
      duration: '45-60 minutes',
      procedure: 'Local anesthesia, infected pulp removal, disinfection, medicament placement, and crown or space maintainer placement.',
      benefits: ['Saves baby teeth', 'Prevents pain', 'Maintains space', 'Guides permanent teeth', 'Preserves function'],
      aftercare: 'Soft foods initially, pain medication as prescribed, maintain oral hygiene, and follow-up visits.'
    },
    {
      id: 13,
      title: 'Behavior Management & Child-Friendly Clinic',
      category: 'pediatric',
      description: 'We ensure a calm, fun environment where children feel comfortable during treatment.',
      features: ['Child-friendly environment', 'Behavior management', 'Comfortable treatment', 'Fun atmosphere'],
      image: 'https://readdy.ai/api/search-image?query=Indian%20pediatric%20dentist%20with%20Indian%20child%20patient%20in%20colorful%20dental%20clinic%2C%20child-friendly%20dental%20environment%20in%20Chennai%2C%20happy%20kids%20dental%20care&width=400&height=300&seq=child-friendly-indian&orientation=landscape',
      icon: 'ri-emotion-happy-line',
      duration: 'Varies by treatment',
      procedure: 'Age-appropriate communication, distraction techniques, positive reinforcement, and gradual treatment introduction.',
      benefits: ['Reduces anxiety', 'Builds trust', 'Positive experience', 'Encourages cooperation', 'Long-term comfort'],
      aftercare: 'Praise child for cooperation, maintain positive attitude about dental visits, and continue regular appointments.'
    },

    // Orthodontics
    {
      id: 14,
      title: 'Metal & Ceramic Braces',
      category: 'orthodontic',
      description: 'Traditional and aesthetic braces to align teeth, correct bite problems, and improve facial profile.',
      features: ['Traditional/aesthetic', 'Teeth alignment', 'Bite correction', 'Facial profile improvement'],
      image: 'https://readdy.ai/api/search-image?query=Indian%20orthodontist%20adjusting%20metal%20braces%20on%20Indian%20patient%2C%20orthodontic%20treatment%20in%20Chennai%20clinic%2C%20teeth%20straightening%20procedure&width=400&height=300&seq=metal-braces-indian&orientation=landscape',
      icon: 'ri-align-center',
      duration: '18-36 months treatment',
      procedure: 'Initial consultation, treatment planning, bracket placement, regular adjustments every 4-6 weeks, and retention phase.',
      benefits: ['Effective alignment', 'Corrects bite issues', 'Improves facial profile', 'Long-term stability', 'Proven results'],
      aftercare: 'Maintain excellent oral hygiene, avoid hard/sticky foods, wear rubber bands as directed, and attend all appointments.'
    },
    {
      id: 15,
      title: 'Clear Aligners / Invisible Braces',
      category: 'orthodontic',
      description: 'Modern, removable, transparent aligners that straighten teeth comfortably without visible brackets.',
      features: ['Transparent', 'Removable', 'Comfortable', 'Invisible treatment'],
      image: 'https://readdy.ai/api/search-image?query=Indian%20orthodontist%20showing%20clear%20aligners%20to%20Indian%20patient%2C%20invisible%20braces%20treatment%20in%20Chennai%20clinic%2C%20transparent%20orthodontic%20therapy&width=400&height=300&seq=clear-aligners-indian&orientation=landscape',
      icon: 'ri-eye-off-line',
      duration: '12-24 months treatment',
      procedure: 'Digital scanning, treatment planning, custom aligner fabrication, progress monitoring, and retention.',
      benefits: ['Nearly invisible', 'Removable for eating', 'Comfortable fit', 'Easy cleaning', 'Predictable results'],
      aftercare: 'Wear 20-22 hours daily, clean aligners regularly, maintain oral hygiene, and change aligners as scheduled.'
    },
    {
      id: 16,
      title: 'Growth Modification & Retainers',
      category: 'orthodontic',
      description: 'Customized treatments for children and teens to guide jaw growth and maintain results after braces.',
      features: ['Growth guidance', 'Jaw development', 'Result maintenance', 'Customized treatment'],
      image: 'https://static.readdy.ai/image/9921b96583d8c2e46a2d40cbefc3b198/2560da2a1aa3a3d1728359c7044fda32.jpeg',
      icon: 'ri-expand-left-right-line',
      duration: 'Varies by age and needs',
      procedure: 'Growth assessment, appliance design, fitting and adjustments, monitoring progress, and transition to retention.',
      benefits: ['Guides proper growth', 'Prevents future problems', 'Maintains results', 'Non-surgical approach', 'Optimal timing'],
      aftercare: 'Wear as directed, maintain appliance cleanliness, attend regular checkups, and follow dietary restrictions.'
    },

    // Oral Surgery
    {
      id: 17,
      title: 'Tooth Extractions (Simple & Surgical)',
      category: 'surgical',
      description: 'Safe and comfortable removal of damaged or impacted teeth, including wisdom tooth extractions.',
      features: ['Safe procedure', 'Comfortable removal', 'Simple/surgical', 'Expert care'],
      image: 'https://readdy.ai/api/search-image?query=Indian%20oral%20surgeon%20performing%20tooth%20extraction%20on%20Indian%20patient%2C%20surgical%20tooth%20removal%20in%20Chennai%20clinic%2C%20professional%20dental%20surgery&width=400&height=300&seq=tooth-extraction-indian&orientation=landscape',
      icon: 'ri-surgical-mask-line',
      duration: '30-60 minutes',
      procedure: 'Local anesthesia, tooth loosening, careful extraction, socket cleaning, and post-operative instructions.',
      benefits: ['Relieves pain', 'Prevents infection spread', 'Safe procedure', 'Quick recovery', 'Expert technique'],
      aftercare: 'Bite on gauze, avoid spitting/rinsing for 24 hours, take prescribed medications, and eat soft foods.'
    },
    {
      id: 18,
      title: 'Wisdom Tooth Removal',
      category: 'surgical',
      description: 'Performed by our oral surgeons with minimal discomfort and fast healing using advanced surgical tools.',
      features: ['Minimal discomfort', 'Fast healing', 'Advanced tools', 'Expert surgeons'],
      image: 'https://readdy.ai/api/search-image?query=Indian%20oral%20surgeon%20removing%20wisdom%20tooth%20from%20Indian%20patient%2C%20impacted%20tooth%20extraction%20in%20Chennai%20clinic%2C%20surgical%20dental%20procedure&width=400&height=300&seq=wisdom-tooth-indian&orientation=landscape',
      icon: 'ri-tooth-line',
      duration: '45-90 minutes',
      procedure: 'Anesthesia administration, tissue reflection, bone removal if needed, tooth sectioning, extraction, and suturing.',
      benefits: ['Prevents crowding', 'Eliminates pain', 'Prevents complications', 'Improves oral health', 'Expert care'],
      aftercare: 'Apply ice packs, take medications as prescribed, eat soft foods, avoid smoking, and attend follow-up.'
    },
    {
      id: 19,
      title: 'Cyst Removal & Jaw Surgery',
      category: 'surgical',
      description: 'For oral infections, cysts, trauma, or jaw deformities — managed by our oral and maxillofacial surgery team.',
      features: ['Infection treatment', 'Cyst removal', 'Trauma care', 'Jaw correction'],
      image: 'https://readdy.ai/api/search-image?query=Indian%20maxillofacial%20surgeon%20performing%20cyst%20removal%20on%20Indian%20patient%2C%20oral%20surgery%20in%20Chennai%20clinic%2C%20jaw%20surgery%20procedure&width=400&height=300&seq=cyst-removal-indian&orientation=landscape',
      icon: 'ri-scissors-cut-line',
      duration: '1-3 hours depending on complexity',
      procedure: 'Comprehensive evaluation, surgical planning, anesthesia, precise surgical removal, and post-operative care.',
      benefits: ['Eliminates pathology', 'Prevents complications', 'Restores function', 'Expert surgical care', 'Comprehensive treatment'],
      aftercare: 'Follow post-surgical instructions, take medications, maintain soft diet, and attend all follow-up appointments.'
    },

    // Dental Implants & Prosthetics
    {
      id: 20,
      title: 'Dental Implants',
      category: 'implants',
      description: 'Permanent tooth replacements that look, feel, and function like natural teeth. Single, multiple, and full-mouth implants available.',
      features: ['Permanent solution', 'Natural function', 'Single/multiple options', 'Leading systems'],
      image: 'https://readdy.ai/api/search-image?query=Indian%20implant%20surgeon%20placing%20dental%20implant%20on%20Indian%20patient%2C%20titanium%20implant%20surgery%20in%20Chennai%20clinic%2C%20professional%20implant%20dentistry&width=400&height=300&seq=dental-implants-indian&orientation=landscape',
      icon: 'ri-tooth-line',
      duration: '3-6 months total treatment',
      procedure: 'Surgical planning, implant placement, healing period, abutment placement, and crown attachment.',
      benefits: ['Permanent solution', 'Natural function', 'Preserves bone', 'No adjacent tooth damage', 'Long-lasting'],
      aftercare: 'Maintain excellent oral hygiene, avoid hard foods initially, attend regular checkups, and follow all instructions.'
    },
    {
      id: 21,
      title: 'Full Mouth Rehabilitation',
      category: 'implants',
      description: 'Comprehensive reconstruction combining crowns, bridges, implants, and bite correction for total oral restoration.',
      features: ['Comprehensive reconstruction', 'Multiple treatments', 'Bite correction', 'Total restoration'],
      image: 'https://readdy.ai/api/search-image?query=Indian%20prosthodontist%20performing%20full%20mouth%20rehabilitation%20on%20Indian%20patient%2C%20complete%20oral%20restoration%20in%20Chennai%20clinic%2C%20comprehensive%20dental%20treatment&width=400&height=300&seq=full-mouth-rehab-indian&orientation=landscape',
      icon: 'ri-refresh-line',
      duration: '6-12 months treatment',
      procedure: 'Comprehensive evaluation, treatment planning, phased treatment execution, bite adjustment, and final restoration.',
      benefits: ['Complete restoration', 'Improved function', 'Enhanced aesthetics', 'Better oral health', 'Comprehensive care'],
      aftercare: 'Follow all post-treatment instructions, maintain excellent oral hygiene, wear night guard if provided, and regular checkups.'
    },
    {
      id: 22,
      title: 'Dentures (Complete & Partial)',
      category: 'implants',
      description: 'Custom-fitted removable prosthetics for patients with missing teeth — comfortable and natural-looking.',
      features: ['Custom-fitted', 'Removable', 'Natural appearance', 'Comfortable wear'],
      image: 'https://readdy.ai/api/search-image?query=Indian%20prosthodontist%20fitting%20dentures%20on%20elderly%20Indian%20patient%2C%20complete%20denture%20placement%20in%20Chennai%20clinic%2C%20prosthetic%20dentistry&width=400&height=300&seq=dentures-indian&orientation=landscape',
      icon: 'ri-contrast-2-line',
      duration: '4-6 weeks fabrication',
      procedure: 'Impressions, bite registration, try-in appointments, adjustments, and final delivery with instructions.',
      benefits: ['Restores function', 'Natural appearance', 'Affordable option', 'Removable for cleaning', 'Custom fit'],
      aftercare: 'Clean daily, remove at night, use denture adhesive if needed, and attend adjustment appointments.'
    },

    // Periodontics
    {
      id: 23,
      title: 'Laser Gum Treatments',
      category: 'periodontics',
      description: 'Minimally invasive laser procedures to treat gum infections, reshape gums, and enhance healing.',
      features: ['Minimally invasive', 'Laser technology', 'Enhanced healing', 'Gum reshaping'],
      image: 'https://readdy.ai/api/search-image?query=Indian%20periodontist%20performing%20laser%20gum%20treatment%20on%20Indian%20patient%2C%20periodontal%20laser%20therapy%20in%20Chennai%20clinic%2C%20minimally%20invasive%20gum%20surgery&width=400&height=300&seq=laser-gum-indian&orientation=landscape',
      icon: 'ri-flashlight-line',
      duration: '60-90 minutes',
      procedure: 'Local anesthesia, laser disinfection, infected tissue removal, root surface cleaning, and healing promotion.',
      benefits: ['Minimally invasive', 'Faster healing', 'Less discomfort', 'Precise treatment', 'Reduced bleeding'],
      aftercare: 'Maintain excellent oral hygiene, use prescribed mouth rinse, avoid smoking, and attend follow-up visits.'
    },
    {
      id: 24,
      title: 'Gum Surgery & Flap Procedures',
      category: 'periodontics',
      description: 'Advanced periodontal treatments for deep pockets, gum recession, and bone loss.',
      features: ['Advanced treatment', 'Deep pocket care', 'Recession treatment', 'Bone preservation'],
      image: 'https://readdy.ai/api/search-image?query=Indian%20periodontist%20performing%20gum%20surgery%20on%20Indian%20patient%2C%20periodontal%20flap%20procedure%20in%20Chennai%20clinic%2C%20advanced%20gum%20treatment&width=400&height=300&seq=gum-surgery-indian&orientation=landscape',
      icon: 'ri-scissors-line',
      duration: '90-120 minutes',
      procedure: 'Local anesthesia, tissue reflection, deep cleaning, bone contouring if needed, and suturing.',
      benefits: ['Eliminates deep pockets', 'Stops bone loss', 'Improves gum health', 'Long-term stability', 'Expert care'],
      aftercare: 'Take prescribed medications, maintain soft diet, gentle oral hygiene, and attend all follow-up visits.'
    },
    {
      id: 25,
      title: 'Crown Lengthening',
      category: 'periodontics',
      description: "Adjusting gum levels to improve the tooth's appearance or prepare for restorative procedures.",
      features: ['Gum level adjustment', 'Appearance improvement', 'Restorative preparation', 'Aesthetic enhancement'],
      image: 'https://readdy.ai/api/search-image?query=Indian%20periodontist%20performing%20crown%20lengthening%20on%20Indian%20patient%2C%20gum%20contouring%20procedure%20in%20Chennai%20clinic%2C%20aesthetic%20gum%20treatment&width=400&height=300&seq=crown-lengthening-indian&orientation=landscape',
      icon: 'ri-ruler-line',
      duration: '60-90 minutes',
      procedure: 'Local anesthesia, gum tissue removal, bone contouring if needed, and suturing for optimal healing.',
      benefits: ['Improves appearance', 'Enables restoration', 'Better oral hygiene', 'Aesthetic enhancement', 'Functional improvement'],
      aftercare: 'Take prescribed medications, maintain soft diet, gentle cleaning, and attend follow-up appointments.'
    },

    // Advanced & Preventive Care
    {
      id: 26,
      title: 'Laser Dentistry',
      category: 'advanced',
      description: 'Used for precise, painless procedures in gum care, cavity removal, teeth whitening, and minor surgeries.',
      features: ['Precise treatment', 'Painless procedures', 'Multiple applications', 'Advanced technology'],
      image: 'https://static.readdy.ai/image/9921b96583d8c2e46a2d40cbefc3b198/5ed75fb44f5a6b983ac5d3ded732540f.jpeg',
      icon: 'ri-flashlight-line',
      duration: 'Varies by procedure',
      procedure: 'Laser calibration, precise tissue targeting, controlled energy delivery, and enhanced healing promotion.',
      benefits: ['Painless treatment', 'Precise control', 'Faster healing', 'Reduced bleeding', 'Minimal discomfort'],
      aftercare: 'Follow specific post-laser instructions, maintain oral hygiene, avoid irritating foods, and attend follow-ups.'
    },
    {
      id: 27,
      title: 'Digital X-Rays & Intraoral Scanning',
      category: 'advanced',
      description: 'Low-radiation imaging for accurate diagnosis and digital impressions for crowns, bridges, and aligners.',
      features: ['Low radiation', 'Accurate diagnosis', 'Digital impressions', 'Instant results'],
      image: 'https://readdy.ai/api/search-image?query=Indian%20dentist%20taking%20digital%20X-ray%20of%20Indian%20patient%2C%20intraoral%20scanner%20in%20Chennai%20clinic%2C%20low%20radiation%20dental%20imaging&width=400&height=300&seq=digital-xray-indian&orientation=landscape',
      icon: 'ri-camera-line',
      duration: '15-30 minutes',
      procedure: 'Digital sensor placement, image capture, computer processing, and immediate image review with patient.',
      benefits: ['Low radiation exposure', 'Instant results', 'Enhanced diagnosis', 'Digital storage', 'Environmental friendly'],
      aftercare: 'No special aftercare required, follow treatment recommendations based on findings.'
    },
    {
      id: 28,
      title: 'Preventive Dentistry',
      category: 'advanced',
      description: 'Includes fluoride treatment, sealants, and patient education to prevent cavities and gum disease.',
      features: ['Fluoride treatment', 'Dental sealants', 'Patient education', 'Disease prevention'],
      image: 'https://readdy.ai/api/search-image?query=Indian%20dentist%20providing%20preventive%20dental%20care%20to%20Indian%20patient%2C%20fluoride%20treatment%20in%20Chennai%20clinic%2C%20oral%20health%20education&width=400&height=300&seq=preventive-dentistry-indian&orientation=landscape',
      icon: 'ri-shield-check-line',
      duration: '30-45 minutes',
      procedure: 'Comprehensive examination, risk assessment, preventive treatments application, and personalized education.',
      benefits: ['Prevents disease', 'Cost-effective', 'Maintains health', 'Early intervention', 'Educational'],
      aftercare: 'Follow oral hygiene recommendations, maintain regular checkups, and implement preventive measures at home.'
    },

    // Specialized Services
    {
      id: 29,
      title: 'TMJ (Jaw Joint) Disorder Treatment',
      category: 'specialized',
      description: 'Relieves pain, clicking, or locking in the jaw joint using splints, physiotherapy, and bite correction.',
      features: ['Pain relief', 'Splint therapy', 'Physiotherapy', 'Bite correction'],
      image: 'https://readdy.ai/api/search-image?query=Indian%20dentist%20treating%20TMJ%20disorder%20in%20Indian%20patient%2C%20jaw%20joint%20therapy%20in%20Chennai%20clinic%2C%20temporomandibular%20joint%20treatment&width=400&height=300&seq=tmj-treatment-indian&orientation=landscape',
      icon: 'ri-skull-line',
      duration: 'Ongoing management',
      procedure: 'Comprehensive evaluation, custom splint fabrication, bite adjustment, and physiotherapy coordination.',
      benefits: ['Pain relief', 'Improved function', 'Non-surgical approach', 'Custom treatment', 'Long-term management'],
      aftercare: 'Wear splint as directed, perform prescribed exercises, avoid hard foods, and attend regular follow-ups.'
    },
    {
      id: 30,
      title: 'Oral Pathology & Biopsies',
      category: 'specialized',
      description: 'Diagnosis and management of oral lesions, ulcers, cysts, and precancerous conditions.',
      features: ['Lesion diagnosis', 'Biopsy procedures', 'Cancer screening', 'Expert management'],
      image: 'https://readdy.ai/api/search-image?query=Indian%20oral%20pathologist%20examining%20Indian%20patient%20mouth%2C%20oral%20lesion%20diagnosis%20in%20Chennai%20clinic%2C%20mouth%20cancer%20screening&width=400&height=300&seq=oral-pathology-indian&orientation=landscape',
      icon: 'ri-microscope-line',
      duration: '30-60 minutes',
      procedure: 'Thorough examination, tissue sampling if needed, laboratory analysis, and result interpretation.',
      benefits: ['Early detection', 'Accurate diagnosis', 'Peace of mind', 'Expert evaluation', 'Timely intervention'],
      aftercare: 'Follow post-biopsy instructions, avoid irritating the area, take prescribed medications, and await results.'
    },
    {
      id: 31,
      title: 'Sleep Apnea & Snoring Devices',
      category: 'specialized',
      description: 'Custom oral appliances to improve airflow and reduce snoring or sleep-related breathing issues.',
      features: ['Custom appliances', 'Improved airflow', 'Snoring reduction', 'Better sleep quality'],
      image: 'https://static.readdy.ai/image/9921b96583d8c2e46a2d40cbefc3b198/adf63ea765cb5ce3ad04982b27ed566c.jpeg',
      icon: 'ri-moon-line',
      duration: '2-3 visits for fitting',
      procedure: 'Sleep study review, custom appliance fabrication, fitting and adjustments, and effectiveness monitoring.',
      benefits: ['Better sleep quality', 'Reduced snoring', 'Non-invasive treatment', 'Custom fit', 'Improved health'],
      aftercare: 'Wear nightly as directed, clean appliance daily, attend adjustment appointments, and monitor effectiveness.'
    },
    {
      id: 32,
      title: 'Emergency Dental Care',
      category: 'specialized',
      description: 'Available for pain, trauma, swelling, or broken teeth — quick relief and expert care when you need it most.',
      features: ['24/7 availability', 'Quick relief', 'Trauma care', 'Expert treatment'],
      image: 'https://static.readdy.ai/image/9921b96583d8c2e46a2d40cbefc3b198/a942a185f3bcb179ac1a961bd32dcad0.jpeg',
      icon: 'ri-alarm-warning-line',
      duration: 'Immediate assessment',
      procedure: 'Rapid assessment, pain management, emergency treatment, and follow-up care planning.',
      benefits: ['Immediate relief', 'Expert emergency care', 'Pain management', 'Trauma treatment', 'Available when needed'],
      aftercare: 'Follow emergency treatment instructions, take prescribed medications, and schedule follow-up care.'
    }
  ];

  const filteredServices = selectedCategory === 'all' 
    ? services 
    : services.filter(service => service.category === selectedCategory);

  const handleBookAppointment = () => {
    window.open(whatsappUrl, '_blank');
  };

  const handleServiceInfo = (service) => {
    setSelectedService(service);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedService(null);
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Dental Services - The Tooth Doctor Advanced Dental Care Centre",
    "description": "Comprehensive dental services including implants, root canal, cosmetic dentistry, orthodontics, and more. Advanced dental treatments in Chennai.",
    "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services`,
    "mainEntity": {
      "@type": "MedicalBusiness",
      "name": "The Tooth Doctor - Advanced Dental Care Centre",
      "medicalSpecialty": [
        "General Dentistry",
        "Cosmetic Dentistry", 
        "Orthodontics",
        "Oral Surgery",
        "Endodontics",
        "Periodontics",
        "Pediatric Dentistry",
        "Prosthodontics"
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Dental Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "MedicalProcedure",
              "name": "Dental Implants",
              "description": "Permanent tooth replacement solution"
            }
          },
          {
            "@type": "Offer", 
            "itemOffered": {
              "@type": "MedicalProcedure",
              "name": "Root Canal Treatment",
              "description": "Pain-free endodontic therapy"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "MedicalProcedure", 
              "name": "Cosmetic Dentistry",
              "description": "Smile enhancement and aesthetic treatments"
            }
          }
        ]
      }
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Services",
          "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services`
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Dental Services - The Tooth Doctor | Comprehensive Dental Care in Chennai"
        description="Comprehensive dental services including implants, root canal, cosmetic dentistry, orthodontics, and more. Advanced dental treatments with expert care in Chennai."
        keywords="dental services Chennai, dental implants, root canal treatment, cosmetic dentistry, orthodontics, teeth whitening, oral surgery"
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services`}
        ogTitle="Dental Services - The Tooth Doctor | Comprehensive Dental Care"
        ogDescription="Comprehensive dental services including implants, root canal, cosmetic dentistry, orthodontics, and more. Expert dental care in Chennai."
        ogUrl={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services`}
        ogImage={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/og-services.jpg`}
        jsonLd={jsonLd}
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#012B53] via-blue-800 to-blue-600 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">Comprehensive & Advanced Dental Treatments</h1>
          <p className="text-xl text-blue-100 max-w-4xl mx-auto mb-8">
            At The Tooth Doctor – Advanced Dental Care Centre, we offer a full range of dental and oral health services under one roof. 
            Our team of experienced dentists and specialists are committed to providing pain-free, precise, and personalized treatment using the latest technology and world-class sterilization standards in <span className="text-yellow-300">Chennai</span>.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" onClick={handleBookAppointment}>
              <i className="ri-calendar-line mr-2"></i>
              Book Appointment
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#012B53]">
              <a href="tel:+916369090441" className="flex items-center">
                <i className="ri-phone-line mr-2"></i>
                Call Now: +91 6369090441
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Service Categories Filter */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Browse Our Comprehensive Services</h2>
            <p className="text-lg text-gray-600">Filter by specialty to find the exact dental care you need</p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {serviceCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-full font-medium transition-all duration-300 whitespace-nowrap text-sm ${
                  selectedCategory === category.id
                    ? 'bg-[#012B53] text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-blue-50 hover:text-[#012B53] shadow-md'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredServices.map((service) => (
              <div key={service.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <div className="w-12 h-12 bg-[#012B53] rounded-full flex items-center justify-center">
                      <i className={`${service.icon} text-white text-xl`}></i>
                    </div>
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-4 text-sm">{service.description}</p>
                  
                  <div className="mb-4">
                    <h4 className="font-medium text-gray-900 mb-2 text-sm">Key Features:</h4>
                    <ul className="space-y-1">
                      {service.features.map((feature, index) => (
                        <li key={index} className="flex items-center text-xs text-gray-600">
                          <i className="ri-check-line text-[#012B53] mr-2"></i>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button variant="primary" size="sm" className="flex-1" onClick={handleBookAppointment}>
                      Book Now
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="px-3"
                      onClick={() => window.open(`https://wa.me/916369090441?text=${encodeURIComponent(`Hello 👋 The Tooth Doctor,\nI'm interested in ${service.title}. Can you provide more information and available slots?`)}`, '_blank')}
                    >
                      <i className="ri-whatsapp-line text-green-500"></i>
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="px-3"
                      onClick={() => handleServiceInfo(service)}
                    >
                      <i className="ri-information-line"></i>
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Service Information Modal */}
      {isModalOpen && selectedService && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">{selectedService.title}</h2>
              <button 
                onClick={closeModal}
                className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
              >
                <i className="ri-close-line text-xl text-gray-600"></i>
              </button>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column - Image and Basic Info */}
                <div>
                  <div className="relative h-64 rounded-xl overflow-hidden mb-6">
                    <img 
                      src={selectedService.image} 
                      alt={selectedService.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-4 left-4">
                      <div className="w-12 h-12 bg-[#012B53] rounded-full flex items-center justify-center">
                        <i className={`${selectedService.icon} text-white text-xl`}></i>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 rounded-xl p-6 mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Treatment Overview</h3>
                    <p className="text-gray-700 mb-4">{selectedService.description}</p>
                    <div className="flex items-center text-sm text-gray-600">
                      <i className="ri-time-line text-[#012B53] mr-2"></i>
                      <span><strong>Duration:</strong> {selectedService.duration}</span>
                    </div>
                  </div>

                  <div className="bg-green-50 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Benefits</h3>
                    <ul className="space-y-2">
                      {selectedService.benefits.map((benefit, index) => (
                        <li key={index} className="flex items-start text-sm text-gray-700">
                          <i className="ri-check-line text-green-600 mr-2 mt-0.5 flex-shrink-0"></i>
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Right Column - Detailed Information */}
                <div>
                  <div className="bg-gray-50 rounded-xl p-6 mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Treatment Procedure</h3>
                    <p className="text-gray-700 text-sm leading-relaxed">{selectedService.procedure}</p>
                  </div>

                  <div className="bg-yellow-50 rounded-xl p-6 mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Features</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {selectedService.features.map((feature, index) => (
                        <div key={index} className="flex items-center text-sm text-gray-700">
                          <i className="ri-star-line text-yellow-600 mr-2"></i>
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-purple-50 rounded-xl p-6 mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Post-Treatment Care</h3>
                    <p className="text-gray-700 text-sm leading-relaxed">{selectedService.aftercare}</p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button 
                      variant="primary" 
                      size="lg" 
                      className="flex-1"
                      onClick={() => {
                        closeModal();
                        handleBookAppointment();
                      }}
                    >
                      <i className="ri-calendar-check-line mr-2"></i>
                      Book This Treatment
                    </Button>
                    <Button 
                      variant="secondary" 
                      size="lg"
                      onClick={() => {
                        closeModal();
                        window.open(`https://wa.me/916369090441?text=${encodeURIComponent(`Hello 👋 The Tooth Doctor,\nI would like to know more about ${selectedService.title}. Can we discuss the treatment details and available slots?`)}`, '_blank');
                      }}
                    >
                      <i className="ri-whatsapp-line mr-2"></i>
                      Ask Questions
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose The Tooth Doctor?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience the difference with our comprehensive approach to dental care
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: 'ri-user-star-line',
                title: 'Expert Specialists',
                description: 'Expert dentists from multiple specialties under one roof'
              },
              {
                icon: 'ri-flashlight-line',
                title: 'Advanced Technology',
                description: 'Painless, digital, and laser-assisted treatments'
              },
              {
                icon: 'ri-money-dollar-circle-line',
                title: 'Affordable Pricing',
                description: 'Affordable, transparent pricing with flexible payment options'
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Hygienic Environment',
                description: 'Hygienic, sterilized environment with world-class standards'
              },
              {
                icon: 'ri-time-line',
                title: 'Same-Day Care',
                description: 'Same-day appointments and emergency care available'
              },
              {
                icon: 'ri-map-pin-line',
                title: 'Convenient Location',
                description: 'Convenient Thiruverkadu, Chennai location with easy access'
              }
            ].map((item, index) => (
              <div key={index} className="text-center p-6 rounded-xl bg-gray-50 hover:bg-blue-50 transition-colors duration-300">
                <div className="w-16 h-16 bg-[#012B53] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${item.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Care Section */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-red-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto">
            <i className="ri-alarm-warning-line text-6xl mb-6"></i>
            <h2 className="text-4xl font-bold mb-4">Dental Emergency?</h2>
            <p className="text-xl mb-8">
              Don't wait! Our emergency dental services are available to provide immediate relief and treatment 
              for urgent dental problems.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:+916369090441" className="inline-flex items-center justify-center px-8 py-4 bg-white text-red-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors">
                <i className="ri-phone-line mr-2"></i>
                Emergency Call: +91 6369090441
              </a>
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-8 py-4 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors">
                <i className="ri-whatsapp-line mr-2"></i>
                WhatsApp Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Ready to Transform Your Smile?</h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Whether it's a routine checkup, a smile makeover, or complex dental surgery — we ensure every patient receives comfortable, affordable, and effective dental care.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={handleBookAppointment}>
              <i className="ri-calendar-check-line mr-2"></i>
              Book Your Appointment
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => window.open('https://share.google/SUIDZUI4yM65Z2LQg', '_blank')}
            >
              <i className="ri-map-pin-line mr-2"></i>
              Visit Our Clinic
            </Button>
          </div>
          <div className="mt-8 text-gray-600">
            <p className="mb-2">
              <i className="ri-map-pin-line text-[#012B53] mr-2"></i>
              Thiruverkadu Bus Terminal, Thiruverkadu, Chennai
            </p>
            <p>
              <i className="ri-time-line text-[#012B53] mr-2"></i>
              Open Monday - Saturday: 9:00 AM - 8:00 PM
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
